/**
 * PosterSpine — a single Event Poster's portfolio folder on the
 * Bookshelf.
 *
 * Physically reads as a flat walnut / tan / oxblood / antique
 * portfolio holding a large print, sitting between the cloth-bound
 * chapter spines and the slim recap-letter folios. The portfolio is
 * WIDER and SHORTER than a chapter — like a folio of prints leaning
 * against the books, not a book itself.
 *
 * Visual language family-tied to ChapterSpine + RecapLetter:
 *   • restrained, museum-like, never skeuomorphic
 *   • subtle ambient breathing, never bouncy
 *   • hover lifts 2-3px only
 *   • foil mark + tiny tag on the front of the portfolio
 *
 * Hard constraint: keep the wall, never reads as "feature card".
 */
import React, { useMemo } from 'react';

const FABRIC_GRADIENTS = {
  walnut:   'linear-gradient(180deg, #5a3a1c 0%, #3a2410 100%)',
  tan:      'linear-gradient(180deg, #8a6238 0%, #5a3e22 100%)',
  oxblood:  'linear-gradient(180deg, #5a1f1c 0%, #3a0f0e 100%)',
  antique:  'linear-gradient(180deg, #7a5a36 0%, #4a3622 100%)',
};
const FABRIC_FOIL = {
  walnut:   '#D4AF5E',
  tan:      '#E6BB55',
  oxblood:  '#E6A85A',
  antique:  '#D9B66A',
};
// Subtle grain inset — the portfolio carries a hint of weft so it
// doesn't read as flat plastic.
const FABRIC_WEAVE = {
  walnut:   'inset 0 0 0 1px rgba(255,235,210,0.06), inset 1.5px 0 0 rgba(255,235,210,0.08)',
  tan:      'inset 0 0 0 1px rgba(255,235,210,0.08), inset 1.5px 0 0 rgba(255,235,210,0.10)',
  oxblood:  'inset 0 0 0 1px rgba(255,235,210,0.05), inset 1.5px 0 0 rgba(255,235,210,0.07)',
  antique:  'inset 0 0 0 1px rgba(255,235,210,0.06), inset 1.5px 0 0 rgba(255,235,210,0.08)',
};

export default function PosterSpine({
  poster,
  shelfHeight = 360,
  onPull,
  entranceDelay = 0,
  focused = false,
}) {
  const spine = poster?.spine || {};
  const fabric = spine.fabric || 'walnut';
  const foil = FABRIC_FOIL[fabric] || '#D4AF5E';
  const height = Math.round((spine.height_pct || 0.72) * shelfHeight);
  // Posters are deliberately wider than chapters so the portfolio
  // reads as containing a large print — clamped so a poster with
  // hundreds of snaps doesn't dominate the shelf.
  const thickness = Math.max(34, Math.min(64, spine.thickness_px || 44));
  const lean = spine.lean_deg || 0;
  const titleOffset = spine.title_offset_px || 0;

  const focusedShadow = focused
    ? `, 0 0 28px 4px ${foil}55, 0 0 0 1px ${foil}88`
    : '';
  const focusedAnim = focused
    ? `, spineFocusGlow 2.6s ease-out ${entranceDelay + 200}ms 3 both`
    : '';

  const style = useMemo(() => ({
    position: 'relative',
    width: thickness,
    height,
    flex: `0 0 ${thickness}px`,
    background: FABRIC_GRADIENTS[fabric] || FABRIC_GRADIENTS.walnut,
    boxShadow: `
      ${FABRIC_WEAVE[fabric] || ''},
      0 10px 22px rgba(0,0,0,0.50),
      0 0 0 1px rgba(0,0,0,0.4),
      inset 0 12px 22px rgba(0,0,0,0.22),
      inset 0 -10px 18px rgba(0,0,0,0.28)
      ${focusedShadow}
    `,
    transform: `rotate(${lean}deg) translateY(${titleOffset}px)`,
    transformOrigin: 'bottom center',
    transition: 'transform 480ms cubic-bezier(0.22, 0.61, 0.36, 1), box-shadow 480ms ease, filter 480ms ease',
    cursor: 'pointer',
    borderRadius: '2px 2px 1px 1px',
    overflow: 'hidden',
    animation: `spineEnter 700ms cubic-bezier(0.22, 0.61, 0.36, 1) ${entranceDelay}ms both, spineBreathe 7s ease-in-out ${entranceDelay + 800}ms infinite${focusedAnim}`,
  }), [thickness, height, fabric, lean, titleOffset, entranceDelay, focused, focusedShadow, focusedAnim]);

  return (
    <button
      type="button"
      onClick={() => onPull && onPull(poster)}
      data-testid={`shelf-poster-${poster.id}`}
      aria-label={`Revisit the poster from ${poster.name}`}
      style={{
        background: 'transparent', border: 'none', padding: 0, margin: 0,
        display: 'inline-block',
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget.querySelector('[data-poster-spine]');
        if (el) {
          el.style.transform = `rotate(${lean * 0.5}deg) translateY(-3px)`;
          el.style.filter = 'brightness(1.08)';
        }
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget.querySelector('[data-poster-spine]');
        if (el) {
          el.style.transform = `rotate(${lean}deg) translateY(${titleOffset}px)`;
          el.style.filter = 'none';
        }
      }}
    >
      <div data-poster-spine style={style}>
        {/* Twin foil bands top + bottom — wider than chapter foil
            bands so the portfolio reads physically distinct on the
            shelf. */}
        <div style={{
          position: 'absolute', top: 14, left: 4, right: 4, height: 3,
          background: foil, opacity: spine.foil === 'embossed' ? 0.85 : 0.55,
        }} />
        <div style={{
          position: 'absolute', bottom: 18, left: 4, right: 4, height: 2,
          background: foil, opacity: spine.foil === 'embossed' ? 0.85 : 0.50,
        }} />

        {/* Centre foil mark — a small "P." monogram so the portfolio
            is identifiable at a glance without ever being branded. */}
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%) rotate(0deg)',
          width: 22, height: 22,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: `1px solid ${foil}`,
          borderRadius: '50%',
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontSize: 11, fontStyle: 'italic',
          color: foil,
          opacity: 0.72,
        }}>P</div>

        {/* Title — sideways, foil-coloured, slightly larger letter
            spacing than chapter titles so the portfolio reads as a
            different artifact class. */}
        <div style={{
          position: 'absolute',
          inset: '38px 0 50px 0',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          writingMode: 'vertical-rl',
          transform: 'rotate(180deg)',
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: '0.26em',
          textTransform: 'uppercase',
          color: foil,
          opacity: 0.92,
          textShadow: `0 0 8px ${foil}22`,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          padding: '0 2px',
        }}>
          The Night, Printed
        </div>

        {/* Tiny snap-count tag near the bottom-left, mimicking the
            handwritten paper tag clipped onto a real portfolio. */}
        {poster.snap_count > 0 && (
          <div style={{
            position: 'absolute', bottom: 28, left: 5,
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: 8, fontStyle: 'italic',
            color: `${foil}aa`,
            letterSpacing: '0.1em',
          }}>
            ✦ {poster.snap_count}
          </div>
        )}
      </div>
    </button>
  );
}
